import {
    ONLINE_OFFER,
    OFFLINE_OFFER,
    EBON_OFFER,
    RESPONSIBLE_OFFER,
    TARGET_ONLINE_OFFERS,
    TARGET_OFFLINE_OFFERS,
    TARGET_EBON_OFFERS,
    TARGET_RESPONSIBLE_OFFERS,
    DEFAULT_TURNOVER_ORDER
} from './constants';

import { hasOffline, hasCashbackOffline, hasCashback, hasEbonCashback } from '../partner/utils';

/**
 * Completes online MEAs list with available online partners
 */
export const completeOnlineMeasWithAvailablePartners = (
    onlineMeas = [],
    partners = {},
    targetCount = TARGET_ONLINE_OFFERS
) => {
    return completeMeasWithAvailablePartners(
        onlineMeas,
        partners,
        targetCount,
        hasCashback,
        (partner, usedIds) =>
            hasCashback(partner) && partner.id !== undefined && !usedIds.has(partner.id),
        ONLINE_OFFER,
        { offline: false }
    );
};

/**
 * Completes offline MEAs list with available offline partners
 */
export const completeOfflineMeasWithAvailablePartners = (
    offlineMeas = [],
    partners = {},
    targetCount = TARGET_OFFLINE_OFFERS
) => {
    return completeMeasWithAvailablePartners(
        offlineMeas,
        partners,
        targetCount,
        hasOffline,
        (partner, usedIds) =>
            hasOffline(partner) &&
            hasCashbackOffline(partner) &&
            partner.id !== undefined &&
            !usedIds.has(partner.id),
        OFFLINE_OFFER,
        { offline: true }
    );
};

/**
 * Completes ebon MEAs list with available ebon partners
 */
export const completeEbonMeasWithAvailablePartners = (
    ebonMeas = [],
    ebonPartners = [],
    targetCount = TARGET_EBON_OFFERS
) => {
    return completeMeasWithAvailablePartners(
        ebonMeas,
        ebonPartners,
        targetCount,
        hasEbonCashback,
        (partner, usedIds) =>
            hasEbonCashback(partner) && partner.id !== undefined && !usedIds.has(partner.id),
        EBON_OFFER,
        { ebon: true, offline: false }
    );
};

/**
 * Completes responsible MEAs list with available partners
 */
export const completeResponsibleMeasWithAvailablePartners = (
    responsibleMeas = [],
    partners = {},
    ebonPartners = [],
    targetCount = TARGET_RESPONSIBLE_OFFERS
) => {
    // For responsible offers, we need to handle both regular partners and ebon partners
    // Mix both partner sources
    const allPartners = [
        ...Object.values(partners).map(p => ({ ...p, isEbon: false })),
        ...ebonPartners.map(p => ({ ...p, isEbon: true }))
    ];

    return completeMeasWithAvailablePartners(
        responsibleMeas,
        allPartners,
        targetCount,
        hasResponsibleCashback,
        (partner, usedIds) =>
            hasResponsibleCashback(partner) && partner.id !== undefined && !usedIds.has(partner.id),
        RESPONSIBLE_OFFER,
        { offline: false }
    );
};

/**
 * Generic function to complete MEAs list with available partners
 * @param {Array} meas - List of MEAs from backend
 * @param {Object} partners - Map of all partners by ID (or array for ebon)
 * @param {number} targetCount - Target number of offers
 * @param {Function} validationFn - Function to validate if partner is valid for this type
 * @param {Function} filterFn - Function to filter available partners
 * @param {number} typeId - MEA type ID
 * @param {Object} extraProps - Extra properties for synthetic MEA
 * @returns {Array} Completed list of MEAs
 */
const completeMeasWithAvailablePartners = (
    meas = [],
    partners = {},
    targetCount,
    validationFn,
    filterFn,
    typeId,
    extraProps = {}
) => {
    // Filter MEAs that have valid active partners
    const validMeas = meas.filter(mea => {
        const partner = Array.isArray(partners)
            ? partners.find(p => p.id === mea.idPartenaire)
            : partners[mea.idPartenaire];
        return validationFn(partner);
    });

    // If we already have enough valid MEAs, return them
    if (validMeas.length >= targetCount) {
        return validMeas;
    }

    // Calculate how many partners we need to add
    const neededCount = targetCount - validMeas.length;

    // Get IDs of partners already used in MEAs
    const usedPartnerIds = new Set(validMeas.map(mea => mea.idPartenaire));

    // Find available partners not already in MEAs
    const partnersArray = Array.isArray(partners) ? partners : Object.values(partners);
    const availablePartners = partnersArray
        .filter(partner => filterFn(partner, usedPartnerIds))
        .sort(sortByRevenue)
        .slice(0, neededCount);

    // If no additional partners available, return what we have
    if (availablePartners.length === 0) {
        return validMeas;
    }

    // Get the template from the first existing MEA (for dates, segment, etc.)
    const template = validMeas.length > 0 ? validMeas[0] : meas[0];

    // Find the max order to continue the sequence
    const maxOrder = validMeas.reduce((max, mea) => {
        return Math.max(max, mea.ordreAffichage || 0);
    }, 0);

    // Create synthetic MEAs for additional partners
    const syntheticMeas = availablePartners.map((partner, index) =>
        createSyntheticMea(partner, index, template, maxOrder, typeId, extraProps)
    );

    // Combine valid MEAs with synthetic ones
    const completedMeas = [...validMeas, ...syntheticMeas];

    // Sort by display order
    return completedMeas.sort((a, b) => a.ordreAffichage - b.ordreAffichage);
};

/**
 * Validates if a partner is valid for responsible offers
 * Can be either a regular partner with cashback or an ebon partner with cashback
 * @param {Object} partner - Partner object (can have isEbon flag)
 * @returns {boolean}
 */
const hasResponsibleCashback = partner => {
    if (!partner) return false;
    if (partner.isEbon) {
        return hasEbonCashback(partner);
    }
    return hasCashback(partner);
};

/**
 * Sorts partners by revenue (turnoverEbcOrder)
 * Lower order = higher revenue
 * @param {Object} a - First partner
 * @param {Object} b - Second partner
 * @returns {number}
 */
const sortByRevenue = (a, b) => {
    const orderA = a.turnoverEbcOrder || DEFAULT_TURNOVER_ORDER;
    const orderB = b.turnoverEbcOrder || DEFAULT_TURNOVER_ORDER;
    return orderA - orderB;
};

/**
 * Creates a synthetic MEA for auto-completion
 * @param {Object} partner - Partner object
 * @param {number} index - Index for synthetic ID generation
 * @param {Object} template - Template MEA for dates and segment
 * @param {number} maxOrder - Maximum order number to continue sequence
 * @param {number} typeId - MEA type ID
 * @param {Object} extraProps - Extra properties to add to the MEA
 * @returns {Object} Synthetic MEA object
 */
const createSyntheticMea = (partner, index, template, maxOrder, typeId, extraProps = {}) => {
    return {
        id: -1000 - index, // Negative ID to mark as synthetic
        typeId,
        idPartenaire: partner.id,
        dateDebut: template ? template.dateDebut : Date.now() / 1000,
        dateFin: template ? template.dateFin : Date.now() / 1000 + 86400 * 30,
        ordreAffichage: maxOrder + index + 1,
        segment: template ? template.segment : 0,
        renvoiFiche: true, // Redirect to partner card
        autopromo: false,
        ...extraProps
    };
};



// WEBPACK FOOTER //
// ./src/services/mea/utils.js